/**
 * Input Component
 *
 * A flexible text input with support for various types, states, and addons.
 */

import React, { forwardRef, useState } from 'react';
import { Icon } from '@iconify/react';
import './Input.css';

const Input = forwardRef(({
  type = 'text',
  size = 'md',
  label,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  readOnly = false,
  error,
  helperText,
  leftIcon,
  rightIcon,
  leftAddon,
  rightAddon,
  fullWidth = false,
  required = false,
  id,
  name,
  className = '',
  inputClassName = '',
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const containerClassNames = [
    'ds-input',
    `ds-input--${size}`,
    fullWidth && 'ds-input--full-width',
    disabled && 'ds-input--disabled',
    error && 'ds-input--error',
    isFocused && 'ds-input--focused',
    className,
  ].filter(Boolean).join(' ');

  const inputId = id || name;

  return (
    <div className={containerClassNames}>
      {label && (
        <label htmlFor={inputId} className="ds-input__label">
          {label}
          {required && <span className="ds-input__required">*</span>}
        </label>
      )}

      <div className="ds-input__wrapper">
        {leftAddon && (
          <div className="ds-input__addon ds-input__addon--left">
            {leftAddon}
          </div>
        )}

        <div className="ds-input__field-wrapper">
          {leftIcon && (
            <span className="ds-input__icon ds-input__icon--left">
              {typeof leftIcon === 'string' ? (
                <Icon icon={leftIcon} />
              ) : (
                leftIcon
              )}
            </span>
          )}

          <input
            ref={ref}
            type={inputType}
            id={inputId}
            name={name}
            value={value}
            defaultValue={defaultValue}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`ds-input__field ${inputClassName}`}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={
              error || helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />

          {isPassword && (
            <button
              type="button"
              className="ds-input__password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} />
            </button>
          )}

          {rightIcon && !isPassword && (
            <span className="ds-input__icon ds-input__icon--right">
              {typeof rightIcon === 'string' ? (
                <Icon icon={rightIcon} />
              ) : (
                rightIcon
              )}
            </span>
          )}
        </div>

        {rightAddon && (
          <div className="ds-input__addon ds-input__addon--right">
            {rightAddon}
          </div>
        )}
      </div>

      {(error || helperText) && (
        <div
          id={`${inputId}-helper`}
          className={`ds-input__helper ${error ? 'ds-input__helper--error' : ''}`}
        >
          {error || helperText}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
